<template>
  <div>
    <loading-overlay v-if="creating" :text="'Ticket wird erstellt...'" />
    
    <!-- Permission Check: Nur Checker (ohne POS/Manager/Admin) haben keinen Zugriff auf POS -->
    <v-container fluid class="px-6 py-6" v-if="isChecker && !isPosUser && !isManager && !isAdmin">
      <v-card class="mb-30">
        <v-card-text class="pa-8 text-center">
          <v-avatar color="warning" size="80" class="mb-4">
            <i class="material-icons-round text-white" style="font-size: 48px;">point_of_sale</i>
          </v-avatar>
          <h3 class="text-h4 font-weight-bold text-typo mb-3">
            Keine Berechtigung
          </h3>
          <p class="text-body-1 text-muted mb-4">
            Du hast Scanner-Berechtigung. Die POS-Funktion steht dir nicht zur Verfügung.
          </p>
          <p class="text-body-2 text-muted mb-6">
            Als Scanner kannst du nur Tickets scannen. Die POS-Funktion ist für POS-Nutzer, Manager oder Administratoren vorgesehen.
          </p>
          <v-btn
            class="bg-gradient-primary text-white"
            large
            @click="$router.push('/').catch(() => {})"
          >
            <i class="material-icons-round me-2">home</i>
            Zur Website
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
    
    <v-container fluid class="px-6 py-6" v-else>
      <!-- Header -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex align-center justify-space-between">
            <h4 class="font-weight-bolder text-h4 text-typo mb-0">
              🎫 POS-Ticket erstellen
            </h4>
            <v-chip
              v-if="currentForManagerId || isRealAdmin"
              color="#00bcd4"
              dark
              small
            >
              Für: {{ getCurrentManagerName() }}
            </v-chip>
          </div>
        </v-col>
      </v-row>

      <!-- Zwei-Spalten-Layout -->
      <v-row>
        <!-- LINKE SPALTE: Formular -->
        <v-col cols="12" lg="8">
          <v-form ref="ticketForm" v-model="formValid">
                <!-- 1️⃣ EVENT AUSWAHL -->
                <v-card class="mb-4 card-shadow border-radius-lg" elevation="2">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center mb-3">
                      <i class="material-icons-round me-2" style="font-size: 24px; color: #cb0c9f;">event</i>
                      <h6 class="font-weight-600 text-typo mb-0">Event auswählen</h6>
                    </div>

                    <label class="label-color font-weight-600 mb-2 d-block">
                      Event
                    </label>
                    <div class="d-flex align-center">
                      <v-autocomplete
                        v-model="selectedEventId"
                        :items="availableEvents"
                        item-text="title"
                        item-value="_id"
                        placeholder="Event suchen..."
                        :rules="eventRules"
                        :multiple="false"
                        hide-details="auto"
                        outlined
                        :loading="loadingEvents || loadingEventDetails"
                        :disabled="loadingEvents || loadingEventDetails"
                        clearable
                        :search-input.sync="eventSearch"
                        no-data-text="Keine Events gefunden"
                        class="input-style font-size-input text-light-input placeholder-light pos-event-autocomplete"
                      >
                        <template v-slot:prepend-inner>
                          <i class="material-icons-round" style="font-size: 18px; color: #67748e;">search</i>
                        </template>
                        
                        <!-- Dropdown Items mit Warnung-Badge -->
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title class="d-flex align-center">
                              {{ item.title }}
                              <v-chip
                                v-if="!item.active || !item.entries_active"
                                x-small
                                color="#00bcd4"
                                dark
                                class="ml-2"
                              >
                                <i class="material-icons-round mr-1" style="font-size: 12px;">warning</i>
                                {{ !item.active ? 'Inaktiv' : 'Anmeldung deaktiviert' }}
                              </v-chip>
                            </v-list-item-title>
                            <v-list-item-subtitle v-if="item.date">
                              {{ formatDate(item.date) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                      <v-btn
                        @click="loadEventsForDropdown"
                        :loading="loadingEvents"
                        :disabled="loadingEvents || loadingEventDetails"
                        icon
                        small
                        class="ml-2"
                        color="primary"
                        title="Events neu laden"
                      >
                        <i class="material-icons-round" style="font-size: 20px;">refresh</i>
                      </v-btn>
                    </div>
                    <!-- Loading Indicator -->
                    <v-card
                      v-if="loadingEventDetails"
                      flat
                      class="mt-3 mb-0 pa-3"
                      style="border-left: 4px solid #17a2b8;"
                    >
                      <div class="d-flex align-center">
                        <v-progress-circular
                          indeterminate
                          color="primary"
                          size="20"
                          width="2"
                          class="me-3"
                        ></v-progress-circular>
                        <div>
                          <p class="mb-0 font-weight-600 text-typo">Event wird geladen...</p>
                          <p class="mb-0 text-caption text-typo">Bitte warten, Event-Details werden abgerufen.</p>
                        </div>
                      </div>
                    </v-card>
                    
                    <!-- Verfügbare Tickets anzeigen -->
                    <v-card
                      v-if="selectedEvent && selectedEvent.slots"
                      flat
                      class="mt-4 pa-3"
                      :style="getAvailableTicketsStyle()"
                    >
                      <div class="d-flex align-center">
                        <i class="material-icons-round me-2" :style="`font-size: 24px; color: ${getAvailableTicketsColor()};`">confirmation_number</i>
                        <div>
                          <p class="mb-0 font-weight-600" style="color: #344767;">
                            Verfügbare Tickets: {{ availableTicketsCount }} / {{ selectedEvent.slots }}
                          </p>
                          <p class="mb-0 text-caption" style="color: #67748e;">
                            <span v-if="availableTicketsCount === 0">Ausverkauft</span>
                            <span v-else-if="availableTicketsCount <= 10">Nur noch {{ availableTicketsCount }} {{ availableTicketsCount === 1 ? 'Ticket' : 'Tickets' }} verfügbar</span>
                            <span v-else>{{ availableTicketsCount }} Tickets verfügbar</span>
                          </p>
                        </div>
                      </div>
                    </v-card>
                    
                    <!-- Warnung für inaktive Events -->
                    <v-card
                      v-if="selectedEvent && (!selectedEvent.active || !selectedEvent.entries_active)"
                      flat
                      class="mt-4 pa-3"
                      style="border-left: 4px solid #fb6340; background-color: #fff5f3;"
                    >
                      <div class="d-flex align-center">
                        <i class="material-icons-round me-2" style="font-size: 24px; color: #fb6340;">warning</i>
                        <div>
                          <p class="mb-0 font-weight-600" style="color: #344767;">
                            {{ !selectedEvent.active ? 'Event ist deaktiviert' : 'Anmeldung ist deaktiviert' }}
                          </p>
                          <p class="mb-0 text-caption" style="color: #67748e;">
                            Dieses Event ist für die Öffentlichkeit nicht sichtbar. 
                            POS-Tickets können trotzdem erstellt werden (z.B. für Reservierungen).
                          </p>
                        </div>
                      </div>
                    </v-card>
                  </v-card-text>
                </v-card>

                <!-- 2️⃣ RECHNUNGSDATEN -->
                <v-card v-if="selectedEvent" class="mb-4 card-shadow border-radius-lg" elevation="2">
                  <v-card-text class="pa-4">
                  <div class="d-flex align-center justify-space-between mb-3">
                    <div class="d-flex align-center">
                      <i class="material-icons-round me-2" style="font-size: 24px; color: #17a2b8;">receipt_long</i>
                      <h6 class="font-weight-600 text-typo mb-0">Rechnungsdaten</h6>
                    </div>
                    <div>
                      <v-btn
                        @click="fillWithDefaultAddress"
                        small
                        outlined
                        color="primary"
                        :disabled="!hasDefaultAddress"
                        class="mr-2"
                      >
                        <i class="material-icons-round me-1" style="font-size: 16px;">content_copy</i>
                        Default übernehmen
                      </v-btn>
                      <v-btn
                        @click="openDefaultAddressDialog"
                        small
                        outlined
                        color="primary"
                      >
                        <i class="material-icons-round me-1" style="font-size: 16px;">settings</i>
                        Default verwalten
                      </v-btn>
                    </div>
                  </div>

                  <v-card
                    v-if="hasDefaultAddress"
                    flat
                    class="mb-4 pa-3"
                    style="border-left: 4px solid #17a2b8; background-color: #e7f6f8;"
                  >
                    <small class="text-typo">
                      <i class="material-icons-round me-1" style="font-size: 14px; vertical-align: middle; color: #17a2b8;">info</i>
                      Die Default-Adresse des Event-Owners wurde geladen. Du kannst sie mit "Default übernehmen" in das Formular einfügen.
                    </small>
                  </v-card>

                  <v-row>
                    <v-col cols="12" md="6">
                      <label class="label-color font-weight-600 mb-2 d-block">
                        Vorname
                      </label>
                      <v-text-field
                        v-model="invoiceData.firstname"
                        placeholder="Max"
                        :rules="invoiceFirstnameRules"
                        hide-details="auto"
                        outlined
                        class="input-style font-size-input text-light-input placeholder-light"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <label class="label-color font-weight-600 mb-2 d-block">
                        Nachname
                      </label>
                      <v-text-field
                        v-model="invoiceData.lastname"
                        placeholder="Mustermann"
                        :rules="invoiceLastnameRules"
                        hide-details="auto"
                        outlined
                        class="input-style font-size-input text-light-input placeholder-light"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <label class="label-color font-weight-600 mb-2 d-block">
                        Straße und Hausnummer
                      </label>
                      <v-text-field
                        v-model="invoiceData.street"
                        placeholder="Musterstraße 123"
                        :rules="streetRules"
                        hide-details="auto"
                        outlined
                        class="input-style font-size-input text-light-input placeholder-light"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="4">
                      <label class="label-color font-weight-600 mb-2 d-block">
                        PLZ
                      </label>
                      <v-text-field
                        v-model="invoiceData.zipcode"
                        placeholder="01234"
                        :rules="zipRules"
                        hide-details="auto"
                        outlined
                        class="input-style font-size-input text-light-input placeholder-light"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="8">
                      <label class="label-color font-weight-600 mb-2 d-block">
                        Stadt
                      </label>
                      <v-text-field
                        v-model="invoiceData.city"
                        placeholder="Musterstadt"
                        :rules="cityRules"
                        hide-details="auto"
                        outlined
                        class="input-style font-size-input text-light-input placeholder-light"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <label class="label-color font-weight-600 mb-2 d-block">
                        E-Mail (Optional)
                      </label>
                      <v-text-field
                        v-model="invoiceData.email"
                        placeholder="email@beispiel.de"
                        type="email"
                        :rules="emailRules"
                        hide-details="auto"
                        outlined
                        class="input-style font-size-input text-light-input placeholder-light"
                      >
                        <template v-slot:prepend-inner v-if="isValidEmail">
                          <i class="material-icons-round" style="font-size: 20px; color: #4caf50;">check_circle</i>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Email-Versand -->
                  <v-row v-if="isValidEmail">
                    <v-col>
                      <v-checkbox
                        v-model="sendEmailAfterCreation"
                        hide-details
                        class="mt-0 pt-0"
                      >
                        <template v-slot:label>
                          <span class="label-color font-weight-600">
                            <i class="material-icons-round me-2" style="font-size: 18px; vertical-align: middle;">email</i>
                            Rechnung und Ticket(s) per E-Mail an {{ invoiceData.email }} senden
                          </span>
                        </template>
                      </v-checkbox>
                    </v-col>
                  </v-row>
                  </v-card-text>
                </v-card>

                <!-- 3️⃣ TICKET-KONFIGURATION -->
                <v-card v-if="selectedEvent" class="mb-4 card-shadow border-radius-lg" elevation="2">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center mb-3">
                      <i class="material-icons-round me-2" style="font-size: 24px; color: #2dce89;">confirmation_number</i>
                      <h6 class="font-weight-600 text-typo mb-0">Ticket-Konfiguration</h6>
                    </div>

                  <v-row>
                  <v-col cols="12" md="6">
                    <label class="label-color font-weight-600 mb-2 d-block">
                      Anzahl Tickets
                    </label>
                    <v-text-field
                      v-model.number="ticketQuantity"
                      type="number"
                      min="1"
                      max="50"
                      placeholder="1"
                      :rules="quantityRules"
                      hide-details="auto"
                      outlined
                      dense
                      class="input-style font-size-input text-light-input placeholder-light"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="6">
                    <label class="label-color font-weight-600 mb-2 d-block">
                      Zahlungsart
                    </label>
                    <v-select
                      v-model="paymentMethod"
                      :items="paymentMethods"
                      item-text="label"
                      item-value="value"
                      hide-details="auto"
                      outlined
                      dense
                      class="input-style font-size-input text-light-input placeholder-light pos-payment-select"
                    >
                      <template v-slot:selection="{ item }">
                        <div class="d-flex align-center">
                          <i class="material-icons-round me-2" style="font-size: 20px; color: #67748e;">{{ item.icon }}</i>
                          <span>{{ item.label }}</span>
                        </div>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title class="d-flex align-center">
                            <i class="material-icons-round me-2" style="font-size: 20px;">{{ item.icon }}</i>
                            <span>{{ item.label }}</span>
                          </v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>

                  <!-- Preis-Auswahl: Online oder Abendkasse -->
                  <v-row v-if="selectedEvent">
                    <v-col cols="12">
                      <label class="label-color font-weight-600 mb-2 d-block">
                        Preis wählen
                      </label>
                      <v-btn-toggle
                        v-model="useEveningPrice"
                        mandatory
                        class="mb-3"
                      >
                        <v-btn :value="false" :class="!useEveningPrice ? 'bg-gradient-primary text-white' : ''">
                          <i class="material-icons-round me-1">shopping_cart</i>
                          Online-Preis: {{ selectedEvent.price.toFixed(2) }} €
                        </v-btn>
                        <v-tooltip bottom max-width="300" v-if="!selectedEvent.price_local || selectedEvent.price_local <= 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn 
                              :value="true" 
                              :class="useEveningPrice ? 'bg-gradient-warning text-white' : ''"
                              :disabled="!selectedEvent.price_local || selectedEvent.price_local <= 0"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <i class="material-icons-round me-1">point_of_sale</i>
                              Abendkasse: {{ selectedEvent.price_local && selectedEvent.price_local > 0 ? selectedEvent.price_local.toFixed(2) + ' €' : 'nicht verfügbar' }}
                            </v-btn>
                          </template>
                          <span>Abendkassenpreis wurde für dieses Event nicht festgelegt</span>
                        </v-tooltip>
                        <v-btn 
                          v-else
                          :value="true" 
                          :class="useEveningPrice ? 'bg-gradient-warning text-white' : ''"
                        >
                          <i class="material-icons-round me-1">point_of_sale</i>
                          Abendkasse: {{ selectedEvent.price_local.toFixed(2) }} €
                        </v-btn>
                      </v-btn-toggle>
                  </v-col>
                </v-row>

                  <!-- Warnung wenn Bankdaten fehlen bei Rechnung -->
                  <v-row v-if="paymentMethod === 'invoice' && !hasCompleteBankDetails">
                    <v-col>
                      <v-card flat class="mt-3 pa-3" style="border-left: 4px solid #fb6340; background-color: #fff5f3;">
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <i class="material-icons-round me-2" style="font-size: 20px; color: #fb6340;">warning</i>
                            <div>
                              <p class="mb-0 font-weight-600" style="color: #344767;">
                                Bankdaten des Event-Owners fehlen
                              </p>
                              <p class="mb-0 text-caption" style="color: #67748e;">
                                Für Zahlungsart "Rechnung" müssen Bankdaten vom Event-Owner hinterlegt werden.
                                <span v-if="!isEventOwner">Nur der Owner kann diese hinterlegen.</span>
                              </p>
                            </div>
                          </div>
                          <v-btn
                            v-if="isEventOwner"
                            @click="openBankDetailsDialog"
                            small
                            class="ml-3 bg-gradient-primary text-white"
                          >
                            <i class="material-icons-round me-1" style="font-size: 16px;">account_balance</i>
                            Bankdaten hinterlegen
                          </v-btn>
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                  
                  <!-- Warnung für POS-User ohne Admin-Rechte -->
                  <v-row v-if="paymentMethod === 'invoice' && !hasCompleteBankDetails && !isAdmin">
                    <v-col>
                      <v-card flat class="mt-3 pa-3" style="border-left: 4px solid #f5365c; background-color: #fff0f0;">
                        <div class="d-flex align-center">
                          <i class="material-icons-round me-2" style="font-size: 20px; color: #f5365c;">error</i>
                          <div>
                            <p class="mb-0 font-weight-600" style="color: #344767;">
                              Bankdaten fehlen
                            </p>
                            <p class="mb-0 text-caption" style="color: #67748e;">
                              Die Zahlungsart "Rechnung" kann nicht verwendet werden. 
                              Bitte kontaktiere den Event-Owner, um Bankdaten zu hinterlegen.
                            </p>
                          </div>
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Ermäßigung aktivieren (nur wenn Event es anbietet und KEINE Personalisierungs-Pflicht) -->
                  <v-row v-if="selectedEvent && selectedEvent.price_lowered && selectedEvent.price_lowered > 0 && !selectedEvent.personalization_required">
                    <v-col>
                      <v-checkbox
                        v-model="usePriceLowered"
                        hide-details
                        class="mt-0 pt-0"
                        @change="onUsePriceLoweredChange"
                      >
                        <template v-slot:label>
                          <span class="label-color font-weight-600">
                            <i class="material-icons-round me-2" style="font-size: 18px; vertical-align: middle;">sell</i>
                            Ermäßigung verwenden ({{ selectedEvent.price_lowered.toFixed(2) }} € statt {{ basePrice.toFixed(2) }} €)
                          </span>
                        </template>
                      </v-checkbox>
                    </v-col>
                  </v-row>
                  </v-card-text>
                </v-card>

                <!-- 4️⃣ PERSONALISIERUNG -->
                <v-card v-if="selectedEvent && (requiresPersonalization || usePriceLowered) && ticketQuantity > 0" class="mb-4 card-shadow border-radius-lg" elevation="2">
                  <v-card-text class="pa-4">
                  <div class="d-flex align-center mb-3">
                    <i class="material-icons-round me-2" style="font-size: 24px; color: #5e72e4;">badge</i>
                    <h6 class="font-weight-600 text-typo mb-0">Personalisierung</h6>
                  </div>
                  <v-card flat class="mb-4 pa-3" v-if="selectedEvent.personalization_required" style="border-left: 4px solid #17a2b8; background-color: #e7f6f8;">
                    <small class="text-typo">
                      <i class="material-icons-round me-1" style="font-size: 14px; vertical-align: middle; color: #17a2b8;">info</i>
                      <strong>Wichtig:</strong> Dieses Event erfordert eine Personalisierung. Bitte gib für jedes Ticket die erforderlichen Daten ein.
                    </small>
                  </v-card>
                  <v-card flat class="mb-4 pa-3" v-if="!selectedEvent.personalization_required && hasAnyPriceLowered" style="border-left: 4px solid #fb6340; background-color: #fff5f3;">
                    <small class="text-typo">
                      <i class="material-icons-round me-1" style="font-size: 14px; vertical-align: middle; color: #fb6340;">info</i>
                      <strong>Ermäßigung gewählt:</strong> Nur die ermäßigten Tickets müssen personalisiert werden. Nicht-ermäßigte Tickets sind optional.
                    </small>
                  </v-card>

                  <div v-for="(participant, index) in participants" :key="index" class="mb-5 pa-4" style="background: #f8f9fa; border-radius: 8px;">
                    <h6 class="font-weight-600 text-typo mb-3">
                      Ticket {{ index + 1 }}
                      <v-chip x-small :color="participant.price_lowered ? 'success' : 'default'" class="ml-2">
                        {{ participant.price_lowered ? `${selectedEvent.price_lowered.toFixed(2)} € (ermäßigt)` : `${basePrice.toFixed(2)} €` }}
                      </v-chip>
                      <v-chip v-if="participant.price_lowered && !selectedEvent.personalization_required" x-small color="error" class="ml-1">
                        Pflicht
                      </v-chip>
                      <v-chip v-if="!participant.price_lowered && !selectedEvent.personalization_required" x-small color="grey lighten-1" class="ml-1">
                        Optional
                      </v-chip>
                    </h6>
                    
                    <!-- Ermäßigung (nur wenn Event es anbietet) -->
                    <v-row v-if="selectedEvent.price_lowered && selectedEvent.price_lowered > 0">
                      <v-col cols="12">
                        <v-checkbox
                          v-model="participant.price_lowered"
                          hide-details
                          class="mt-0"
                          @change="onPriceLoweredChange"
                        >
                          <template v-slot:label>
                            <span class="label-color font-weight-600">
                              Ermäßigt
                              <v-tooltip bottom max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-btn
                                    icon
                                    x-small
                                    v-bind="attrs"
                                    v-on="on"
                                    class="ml-1"
                                  >
                                    <i class="material-icons-round" style="font-size: 16px; color: #67748e;">info</i>
                                  </v-btn>
                                </template>
                                <span>{{ selectedEvent.price_lowered_info || 'Ermäßigter Preis verfügbar (z.B. für Kinder, Schüler, Rentner, Menschen mit Behinderung)' }}</span>
                              </v-tooltip>
                            </span>
                          </template>
                        </v-checkbox>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12">
                        <label class="label-color font-weight-600 mb-2 d-block">
                          Vor- und Nachname *
                        </label>
                        <v-text-field
                          v-model="participant.name"
                          placeholder="Vor- und Nachname"
                          :rules="nameRules"
                          hide-details="auto"
                          outlined
                          dense
                          class="input-style font-size-input text-light-input placeholder-light"
                        >
                          <template v-slot:prepend-inner>
                            <i class="material-icons-round" style="font-size: 20px; color: #67748e;">badge</i>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12">
                        <label class="label-color font-weight-600 mb-2 d-block">
                          Straße & Hausnummer {{ selectedEvent.address_required ? '*' : '(optional)' }}
                        </label>
                        <v-text-field
                          v-model="participant.street"
                          placeholder="Straße & Hausnummer"
                          :rules="selectedEvent.address_required ? streetRules : []"
                          hide-details="auto"
                          outlined
                          dense
                          class="input-style font-size-input text-light-input placeholder-light"
                        >
                          <template v-slot:prepend-inner>
                            <i class="material-icons-round" style="font-size: 20px; color: #67748e;">home</i>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" md="6">
                        <label class="label-color font-weight-600 mb-2 d-block">
                          Postleitzahl {{ selectedEvent.address_required ? '*' : '(optional)' }}
                        </label>
                        <v-text-field
                          v-model="participant.zipcode"
                          placeholder="PLZ"
                          :rules="selectedEvent.address_required ? zipRules : []"
                          hide-details="auto"
                          outlined
                          dense
                          class="input-style font-size-input text-light-input placeholder-light"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <label class="label-color font-weight-600 mb-2 d-block">
                          Ort {{ selectedEvent.address_required ? '*' : '(optional)' }}
                        </label>
                        <v-text-field
                          v-model="participant.city"
                          placeholder="Ort"
                          :rules="selectedEvent.address_required ? cityRules : []"
                          hide-details="auto"
                          outlined
                          dense
                          class="input-style font-size-input text-light-input placeholder-light"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </div>
                  </v-card-text>
                </v-card>

                <!-- 5️⃣ RABATT GEWÄHREN -->
              </v-form>
        </v-col>

        <!-- RECHTE SPALTE: Sticky Summary -->
        <v-col cols="12" lg="4">
          <div style="position: sticky; top: 100px;">
            <!-- Summary Card -->
            <v-card v-if="selectedEvent" class="card-shadow border-radius-lg mb-3" elevation="2">
              <div class="bg-gradient-default pa-4 border-radius-lg-top">
                <h6 class="text-white mb-0 font-weight-bold">
                  📊 Zusammenfassung
                </h6>
              </div>
              <v-card-text class="pa-4">
                <!-- Event Info -->
                <div class="mb-3 pb-3" style="border-bottom: 1px solid #f0f0f0;">
                  <p class="mb-1 text-sm text-typo">
                    <strong>Event:</strong>
                  </p>
                  <p class="mb-0 text-sm text-secondary">{{ selectedEvent.title }}</p>
                  <p class="mb-0 text-xs text-secondary">{{ formatDate(selectedEvent.date) }}</p>
                </div>

                <!-- Ticket Info -->
                <div class="mb-3 pb-3" style="border-bottom: 1px solid #f0f0f0;">
                  <p class="mb-1 text-sm"><strong>Anzahl:</strong> {{ ticketQuantity }} Ticket(s)</p>
                  <p class="mb-1 text-sm" v-if="invoiceData.firstname && invoiceData.lastname">
                    <strong>Rechnung an:</strong> {{ invoiceData.firstname }} {{ invoiceData.lastname }}
                  </p>
                  <p class="mb-0 text-sm" v-if="requiresPersonalization || usePriceLowered">
                    <strong>Personalisierung:</strong> {{ actualFilledRequiredCount }}/{{ requiredFilledCount }}
                  </p>
                  <p class="mb-0 text-sm" v-if="priceLoweredCount > 0">
                    <strong>Ermäßigungen:</strong> {{ priceLoweredCount }} Ticket(s)
                  </p>
                </div>

                <!-- Preis-Aufschlüsselung -->
                <div class="mb-3">
                  <!-- Positionenliste -->
                  <div class="mb-3 pb-3" style="border-bottom: 1px solid #f0f0f0;" v-if="hasDifferentPrices && ticketPositions.length > 0">
                    <p class="mb-2 text-sm font-weight-bold">
                      <i class="material-icons-round me-1" style="font-size: 16px; vertical-align: middle; color: #5e72e4;">receipt_long</i>
                      Positionen:
                    </p>
                    <div v-for="(position, index) in ticketPositions" :key="index" class="mb-2">
                      <p class="mb-0 text-sm text-typo">
                        {{ position.quantity }}x {{ position.description }}
                        <span class="float-end">{{ (position.price * position.quantity).toFixed(2) }} €</span>
                      </p>
                    </div>
                  </div>
                  
                  <p class="mb-1 text-sm">
                    <strong>Zwischensumme:</strong>
                    <span class="float-end">{{ subtotalPrice.toFixed(2) }} €</span>
                  </p>
                  <p class="mb-1 text-sm" :class="discountMode === 'discount' ? 'text-success' : 'text-warning'" v-if="discountValue > 0">
                    <strong>{{ discountMode === 'discount' ? 'Rabatt' : 'Aufschlag' }} ({{ discountPercentageValue.toFixed(1) }}%):</strong>
                    <span class="float-end">{{ discountMode === 'discount' ? '-' : '+' }}{{ discountAmountEuro.toFixed(2) }} €</span>
                  </p>
                  <div style="border-top: 2px solid #344767;" class="mt-2 pt-2">
                    <p class="mb-0 text-h6 font-weight-bold">
                      GESAMT:
                      <span class="float-end">{{ finalTotalPrice.toFixed(2) }} €</span>
                    </p>
                  </div>
                </div>

                <!-- Rabatt/Aufschlag-Eingabe -->
                <div class="mb-3 pb-3" style="border-bottom: 1px solid #f0f0f0;">
                  <p class="mb-2 text-sm font-weight-bold">
                    <i class="material-icons-round me-1" style="font-size: 16px; vertical-align: middle; color: #f5365c;">sell</i>
                    {{ discountMode === 'discount' ? 'Rabatt' : 'Aufschlag' }} gewähren
                  </p>
                  
                  <!-- Art der Anpassung -->
                  <v-btn-toggle
                    v-model="discountMode"
                    mandatory
                    class="mb-3"
                    dense
                  >
                    <v-btn value="discount" :class="discountMode === 'discount' ? 'bg-gradient-success text-white' : ''" small>
                      <i class="material-icons-round me-1" style="font-size: 16px;">trending_down</i>
                      Rabatt
                    </v-btn>
                    <v-btn value="surcharge" :class="discountMode === 'surcharge' ? 'bg-gradient-warning text-white' : ''" small>
                      <i class="material-icons-round me-1" style="font-size: 16px;">trending_up</i>
                      Aufschlag
                    </v-btn>
                  </v-btn-toggle>
                  
                  <v-text-field
                    v-model.number="discountValue"
                    type="number"
                    min="0"
                    :step="discountType === 'percent' ? '1' : '0.01'"
                    :max="discountType === 'percent' ? (discountMode === 'discount' ? '100' : undefined) : undefined"
                    :rules="discountRules"
                    placeholder="0"
                    hide-details="auto"
                    outlined
                    dense
                    class="mb-2"
                  >
                    <template v-slot:prepend-inner>
                      <i class="material-icons-round" style="font-size: 18px; color: #67748e;">{{ discountMode === 'discount' ? 'trending_down' : 'trending_up' }}</i>
                    </template>
                    <template v-slot:append>
                      <v-btn-toggle
                        v-model="discountType"
                        mandatory
                        dense
                        class="mt-0"
                      >
                        <v-btn 
                          x-small 
                          value="fixed" 
                          style="min-width: 40px;"
                          :class="discountType === 'fixed' ? 'bg-gradient-info text-white font-weight-bold' : 'bg-light'"
                        >
                          €
                        </v-btn>
                        <v-btn 
                          x-small 
                          value="percent" 
                          style="min-width: 40px;"
                          :class="discountType === 'percent' ? 'bg-gradient-info text-white font-weight-bold' : 'bg-light'"
                        >
                          %
                        </v-btn>
                      </v-btn-toggle>
                    </template>
                  </v-text-field>
                  <v-text-field
                    v-model="discountReason"
                    placeholder="Grund (optional)"
                    hide-details="auto"
                    outlined
                    dense
                  >
                    <template v-slot:prepend-inner>
                      <i class="material-icons-round" style="font-size: 18px; color: #67748e;">description</i>
                    </template>
                  </v-text-field>
                  <p class="mb-0 text-xs text-secondary mt-1">
                    Diese Information wird zum {{ discountMode === 'discount' ? 'Rabatt' : 'Aufschlag' }} auf der Rechnung dargestellt.
                  </p>
                </div>

                <!-- Zahlungsart & Email -->
                <div class="mb-3 pb-3" style="border-bottom: 1px solid #f0f0f0;">
                  <p class="mb-1 text-sm">
                    <i class="material-icons-round me-1" style="font-size: 14px; vertical-align: middle;">{{ paymentMethod === 'cash' ? 'payments' : paymentMethod === 'electronic' ? 'credit_card' : 'description' }}</i>
                    {{ paymentMethodLabel }}
                    <span v-if="selectedEvent && selectedEvent.price_local && selectedEvent.price_local > 0" class="ml-2">
                      • 
                      <i class="material-icons-round me-1" style="font-size: 14px; vertical-align: middle;">{{ useEveningPrice ? 'point_of_sale' : 'shopping_cart' }}</i>
                      {{ useEveningPrice ? 'Abendkassen-Preis' : 'Online-Preis' }}
                    </span>
                  </p>
                  <p class="mb-0 text-sm text-success" v-if="sendEmailAfterCreation">
                    <i class="material-icons-round me-1" style="font-size: 14px; vertical-align: middle;">email</i>
                    Email-Versand aktiviert
                  </p>
                </div>

                <!-- Validation Issues -->
                <v-card
                  v-if="validationIssues.length > 0"
                  flat
                  class="mb-3 pa-3"
                  style="border-left: 4px solid #f5365c; background-color: #fff5f3;"
                >
                  <p class="mb-2 text-sm font-weight-bold" style="color: #f5365c;">
                    <i class="material-icons-round me-1" style="font-size: 16px; vertical-align: middle;">checklist</i>
                    Noch zu erledigen:
                  </p>
                  <ul class="mb-0 pl-3" style="list-style: none;">
                    <li v-for="(issue, index) in validationIssues" :key="index" class="mb-1 text-xs">
                      <i class="material-icons-round me-1" :style="`font-size: 14px; vertical-align: middle; color: ${issue.color};`">
                        {{ issue.icon }}
                      </i>
                      {{ issue.text }}
                    </li>
                  </ul>
                </v-card>

                <!-- Warnung -->
                <v-card
                  v-if="selectedEvent && (!selectedEvent.active || !selectedEvent.entries_active)"
                  flat
                  class="mb-3 pa-2"
                  style="border-left: 3px solid #fb6340; background-color: rgba(251, 99, 64, 0.1);"
                >
                  <small class="text-xs">
                    <i class="material-icons-round me-1" style="font-size: 12px; vertical-align: middle; color: #fb6340;">info</i>
                    {{ !selectedEvent.active ? 'Event nicht öffentlich' : 'Anmeldung nicht öffentlich' }}
                  </small>
                </v-card>

                <!-- Sticky Button -->
                <v-btn
                  block
                  elevation="0"
                  height="50"
                  class="font-weight-bold text-uppercase bg-gradient-success text-white"
                  @click="showConfirmationModal"
                  :disabled="!canCreateTicket"
                  :loading="creating"
                >
                  <i class="material-icons-round me-2">receipt</i>
                  {{ ticketQuantity > 1 ? `${ticketQuantity} Tickets` : 'Ticket' }} erstellen
                </v-btn>
              </v-card-text>
            </v-card>

            <!-- Platzhalter wenn kein Event -->
            <v-card v-else class="card-shadow border-radius-lg text-center pa-5" elevation="1">
              <i class="material-icons-round mb-2" style="font-size: 48px; color: #ccc;">event_note</i>
              <p class="text-secondary mb-0">Wähle zuerst ein Event aus</p>
            </v-card>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- PDF Download Dialog -->
    <v-dialog v-model="showPdfDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <h5 class="text-h5 font-weight-bold text-typo">
            {{ ticketResults.length > 1 
              ? `${ticketResults.length} Tickets erfolgreich erstellt!` 
              : 'Ticket erfolgreich erstellt!' }}
          </h5>
        </v-card-title>

        <v-card-text>
          <p class="mb-4 text-typo">
            {{ ticketResults.length > 1 
              ? `${ticketResults.length} POS-Tickets wurden erfolgreich erstellt.` 
              : 'Das POS-Ticket wurde erfolgreich erstellt.' }}
          </p>

          <v-alert type="success" class="mb-4">
            <div v-if="ticketResults.length === 1">
              <strong>Ticket-Details:</strong><br>
              Event: {{ ticketResult && ticketResult.event }}<br>
              Teilnehmer: {{ ticketResult && ticketResult.participant }}<br>
              Rechnungsnr: {{ ticketResult && ticketResult.invoiceNr }}
            </div>
            <div v-else>
              <strong>{{ ticketResults.length }} Tickets erstellt:</strong><br>
              Event: {{ ticketResults[0] && ticketResults[0].event }}<br>
              Rechnungsnr: {{ ticketResults[0] && ticketResults[0].invoiceNr }}
            </div>
          </v-alert>

          <v-alert type="info" dense text class="mb-0">
            <small class="text-typo">
              <strong>1 Rechnung erstellt</strong> – Du kannst die Rechnung inkl. aller Tickets als kombinierte PDF herunterladen.
            </small>
          </v-alert>
        </v-card-text>

        <v-card-actions class="pt-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            v-if="paymentId"
            elevation="0"
            height="40"
            class="bg-gradient-primary text-white font-weight-bold text-uppercase px-4"
            :loading="downloadingPdf"
            @click="downloadInvoice"
          >
            <i class="material-icons-round me-2" style="font-size: 18px;">download</i>
            Rechnung + Tickets
          </v-btn>
          <v-btn
            elevation="0"
            height="40"
            class="bg-gradient-info text-white font-weight-bold text-uppercase px-4"
            @click="goToTickets"
          >
            <i class="material-icons-round me-2" style="font-size: 18px;">confirmation_number</i>
            Zur Übersicht
          </v-btn>
          <v-btn
            text
            height="40"
            class="text-secondary font-weight-bold text-uppercase px-4"
            @click="closeDialog"
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Default-Adresse Modal -->
    <v-dialog v-model="showDefaultAddressDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <h5 class="text-h5 font-weight-bold text-typo mb-0">
            <i class="material-icons-round me-2" style="font-size: 24px; vertical-align: middle;">edit_location</i>
            Default-Adresse für Bulk-Tickets
          </h5>
        </v-card-title>
        
        <v-card-text class="pt-4">
          <v-alert type="info" dense text class="mb-4">
            <small class="text-typo">
              <strong>Hinweis:</strong> Diese Einstellung speichert deine persönliche Default-Adresse als Admin.
              <br>
              <strong>Bei POS-Verkäufen wird automatisch die Default-Adresse des Event-Owners verwendet.</strong>
            </small>
          </v-alert>

          <v-form ref="defaultAddressForm">
            <v-row>
              <v-col cols="12" md="6">
                <label class="text-typo font-weight-600 mb-2 d-block">
                  Vorname
                </label>
                <v-text-field
                  v-model="defaultAddress.firstname"
                  placeholder="Max"
                  outlined
                  dense
                  :rules="[v => !!v || 'Vorname ist erforderlich']"
                  class="input-style font-size-input text-light-input placeholder-light"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <label class="text-typo font-weight-600 mb-2 d-block">
                  Nachname
                </label>
                <v-text-field
                  v-model="defaultAddress.lastname"
                  placeholder="Mustermann"
                  outlined
                  dense
                  :rules="[v => !!v || 'Nachname ist erforderlich']"
                  class="input-style font-size-input text-light-input placeholder-light"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12">
                <label class="text-typo font-weight-600 mb-2 d-block">
                  Straße & Hausnummer
                </label>
                <v-text-field
                  v-model="defaultAddress.street"
                  placeholder="z.B. Musterstraße 123"
                  outlined
                  dense
                  :rules="[v => !!v || 'Straße ist erforderlich']"
                  class="input-style font-size-input text-light-input placeholder-light"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="4">
                <label class="text-typo font-weight-600 mb-2 d-block">
                  PLZ
                </label>
                <v-text-field
                  v-model="defaultAddress.zipcode"
                  placeholder="z.B. 12345"
                  outlined
                  dense
                  :rules="[v => !!v || 'PLZ ist erforderlich']"
                  class="input-style font-size-input text-light-input placeholder-light"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="8">
                <label class="text-typo font-weight-600 mb-2 d-block">
                  Stadt
                </label>
                <v-text-field
                  v-model="defaultAddress.city"
                  placeholder="z.B. Musterstadt"
                  outlined
                  dense
                  :rules="[v => !!v || 'Stadt ist erforderlich']"
                  class="input-style font-size-input text-light-input placeholder-light"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeDefaultAddressDialog"
            :disabled="savingDefaultAddress"
            class="me-2"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">close</i>
            Abbrechen
          </v-btn>
          <v-btn
            :loading="savingDefaultAddress"
            @click="saveDefaultAddress"
            class="bg-gradient-primary text-white"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">save</i>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bankdaten Modal -->
    <v-dialog v-model="showBankDetailsDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <h5 class="text-h5 font-weight-bold text-typo mb-0">
            <i class="material-icons-round me-2" style="font-size: 24px; vertical-align: middle;">account_balance</i>
            Bankdaten für Rechnungen
          </h5>
        </v-card-title>
        
        <v-card-text class="pt-4">
          <v-alert type="info" dense colored-border border="left" class="mb-4">
            <small class="text-typo">
              <i class="material-icons-round me-1" style="font-size: 14px; vertical-align: middle;">info</i>
              <strong>Hinweis:</strong> Du bearbeitest die Bankdaten des Event-Owners. 
              Diese werden bei POS-Verkäufen mit Zahlungsart "Rechnung" verwendet.
            </small>
          </v-alert>
          <v-alert type="warning" dense colored-border border="left" class="mb-4">
            <small class="text-typo">
              <strong>Wichtig:</strong> Bitte prüfe alle Angaben sorgfältig auf Richtigkeit.
            </small>
          </v-alert>

          <v-form ref="bankDetailsForm" v-model="bankDetailsValid">
            <v-row>
              <v-col cols="12">
                <label class="text-typo font-weight-600 mb-2 d-block">
                  Kontoinhaber / Empfängername <span class="text-danger">*</span>
                </label>
                <v-text-field
                  v-model="bankDetails.accountHolder"
                  placeholder="z.B. Max Mustermann oder Firma GmbH"
                  :rules="accountHolderRules"
                  hide-details="auto"
                  outlined
                  dense
                  class="input-style font-size-input text-light-input placeholder-light"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <label class="text-typo font-weight-600 mb-2 d-block">
                  IBAN <span class="text-danger">*</span>
                </label>
                <v-text-field
                  v-model="bankDetails.iban"
                  placeholder="DE89 3704 0044 0532 0130 00"
                  :rules="ibanRules"
                  hide-details="auto"
                  outlined
                  dense
                  class="input-style font-size-input text-light-input placeholder-light"
                  @input="formatIBAN"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <label class="text-typo font-weight-600 mb-2 d-block">
                  BIC / SWIFT-Code
                </label>
                <v-text-field
                  v-model="bankDetails.bic"
                  placeholder="z.B. COBADEFFXXX"
                  :rules="bicRules"
                  hide-details="auto"
                  outlined
                  dense
                  class="input-style font-size-input text-light-input placeholder-light"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <label class="text-typo font-weight-600 mb-2 d-block">
                  Bankname
                </label>
                <v-text-field
                  v-model="bankDetails.bankName"
                  placeholder="z.B. Sparkasse Musterstadt"
                  hide-details
                  outlined
                  dense
                  class="input-style font-size-input text-light-input placeholder-light"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <label class="text-typo font-weight-600 mb-2 d-block">
                  Zahlungsziel (Tage)
                </label>
                <v-text-field
                  v-model.number="bankDetails.paymentTerm"
                  type="number"
                  min="1"
                  max="90"
                  placeholder="14"
                  :rules="paymentTermRules"
                  hide-details="auto"
                  outlined
                  dense
                  class="input-style font-size-input text-light-input placeholder-light"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <label class="text-typo font-weight-600 mb-2 d-block">
                  Zusätzliche Hinweise für Rechnung
                </label>
                <v-textarea
                  v-model="bankDetails.invoiceNotes"
                  placeholder="z.B. Bei Fragen zur Rechnung kontaktieren Sie uns unter..."
                  hide-details
                  outlined
                  rows="3"
                  dense
                  class="input-style font-size-input text-light-input placeholder-light"
                ></v-textarea>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Bestätigungs-Checkbox -->
            <v-row>
              <v-col cols="12">
                <v-checkbox
                  v-model="bankDetailsConfirmed"
                  hide-details
                  class="mt-0 pt-0"
                >
                  <template v-slot:label>
                    <span class="text-typo font-weight-600">
                      <i class="material-icons-round me-2 text-warning" style="font-size: 18px; vertical-align: middle;">verified_user</i>
                      Ich bestätige, dass alle eingegebenen Bankdaten korrekt und vollständig sind. 
                      Diese Daten werden auf Rechnungen für Kunden sichtbar sein.
                    </span>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4 d-flex justify-end">
          <v-btn
            text
            @click="closeBankDetailsDialog"
            :disabled="savingBankDetails"
            class="me-2"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">close</i>
            Abbrechen
          </v-btn>
          <v-btn
            :loading="savingBankDetails"
            :disabled="!bankDetailsValid || !bankDetailsConfirmed"
            @click="saveBankDetails"
            class="bg-gradient-primary text-white"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">save</i>
            Bankdaten speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Modal -->
    <v-dialog v-model="showConfirmDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <h5 class="text-h5 font-weight-bold text-typo mb-0">
            <i class="material-icons-round me-2" style="font-size: 24px; vertical-align: middle;">check_circle</i>
            Ticket-Erstellung bestätigen
          </h5>
        </v-card-title>
        
        <v-card-text class="pt-4">
          <v-alert type="info" dense colored-border border="left" class="mb-4">
            <small class="text-typo">
              Bitte überprüfe alle Angaben vor der Erstellung. Nach Bestätigung werden die Tickets erstellt.
            </small>
          </v-alert>

          <!-- Event Info -->
          <v-card outlined class="pa-3 mb-4" elevation="0">
            <p class="mb-1 text-typo font-weight-bold" style="font-size: 1.1rem;">
              <i class="material-icons-round me-2" style="font-size: 20px; vertical-align: middle; color: #5e72e4;">event</i>
              {{ selectedEvent ? selectedEvent.title : '-' }}
            </p>
            <p class="mb-0 text-sm text-secondary">
              <i class="material-icons-round me-1" style="font-size: 16px; vertical-align: middle;">calendar_today</i>
              {{ selectedEvent && selectedEvent.date ? formatDate(selectedEvent.date) : '-' }}
            </p>
          </v-card>

          <!-- Rechnungsaufschlüsselung -->
          <v-card outlined class="pa-4 mb-4" elevation="0">
            <h6 class="text-typo font-weight-600 mb-3">
              <i class="material-icons-round me-2" style="font-size: 20px; vertical-align: middle; color: #5e72e4;">receipt_long</i>
              Rechnungsaufschlüsselung
            </h6>
            
            <!-- Positionen -->
            <div class="mb-3">
              <!-- Immer einzelne Positionen anzeigen, wenn verschiedene Preise vorhanden sind -->
              <template v-if="hasDifferentPrices">
                <div 
                  v-for="(position, index) in ticketPositions" 
                  :key="index"
                  class="d-flex justify-space-between mb-2 pb-2" 
                  style="border-bottom: 1px solid #e9ecef;"
                >
                  <div>
                    <p class="mb-0 text-typo font-weight-600">
                      {{ position.quantity }}x {{ position.description }}
                    </p>
                    <p class="mb-0 text-xs text-secondary">
                      Einzelpreis: {{ position.price.toFixed(2) }} €
                    </p>
                  </div>
                  <div class="text-right">
                    <p class="mb-0 text-typo font-weight-600">{{ (position.price * position.quantity).toFixed(2) }} €</p>
                  </div>
                </div>
              </template>
              <!-- Wenn alle Tickets den gleichen Preis haben, eine Position anzeigen -->
              <template v-else>
                <div class="d-flex justify-space-between mb-2 pb-2" style="border-bottom: 1px solid #e9ecef;">
                  <div>
                    <p class="mb-0 text-typo font-weight-600">
                      {{ ticketQuantity }}x {{ getSinglePositionDescription }}
                    </p>
                    <p class="mb-0 text-xs text-secondary">
                      Einzelpreis: {{ basePrice.toFixed(2) }} €
                    </p>
                  </div>
                  <div class="text-right">
                    <p class="mb-0 text-typo font-weight-600">{{ subtotalPrice.toFixed(2) }} €</p>
                  </div>
                </div>
              </template>
            </div>
            
            <!-- Rabatt/Aufschlag -->
            <div v-if="discountValue > 0" class="mb-3">
              <div class="d-flex justify-space-between mb-2 pb-2" style="border-bottom: 1px solid #e9ecef;">
            <div>
                  <p class="mb-0 text-typo">
                    <i class="material-icons-round me-1" style="font-size: 16px; vertical-align: middle; color: #67748e;">{{ discountMode === 'discount' ? 'trending_down' : 'trending_up' }}</i>
                    <strong>{{ discountMode === 'discount' ? 'Rabatt' : 'Aufschlag' }}</strong>
                    <span v-if="discountReason" class="text-xs text-secondary d-block mt-1">
                      {{ discountReason }}
                </span>
                  </p>
                  <p class="mb-0 text-xs text-secondary">
                    {{ discountType === 'percent' ? discountValue + '%' : discountValue.toFixed(2) + ' €' }} 
                    ({{ discountPercentageValue.toFixed(1) }}%)
                  </p>
                </div>
                <div class="text-right">
                  <p class="mb-0 text-typo" :class="discountMode === 'discount' ? 'text-success' : 'text-warning'">
                    {{ discountMode === 'discount' ? '-' : '+' }}{{ discountAmountEuro.toFixed(2) }} €
                  </p>
                </div>
              </div>
            </div>
            
            <!-- Gesamtsumme -->
            <v-divider class="my-2"></v-divider>
            <div class="d-flex justify-space-between pt-2">
              <p class="mb-0 text-typo font-weight-bold" style="font-size: 1.1rem;">
                GESAMTSUMME:
              </p>
              <p class="mb-0 text-typo font-weight-bold" style="font-size: 1.1rem;">
                {{ finalTotalPrice.toFixed(2) }} €
              </p>
            </div>
          </v-card>

          <!-- Zusätzliche Informationen (nicht rechnungsrelevant) -->
          <v-card outlined class="pa-3 mb-3" elevation="0">
            <h6 class="text-typo font-weight-600 mb-3">
              <i class="material-icons-round me-2" style="font-size: 18px; vertical-align: middle; color: #67748e;">info</i>
              Zusätzliche Informationen
            </h6>
            <div>
              <p class="mb-2 text-sm text-typo">
                <strong>Zahlungsart:</strong> {{ paymentMethodLabel }}
              </p>
              <p class="mb-2 text-sm text-typo" v-if="sendEmailAfterCreation">
                <i class="material-icons-round me-1" style="font-size: 14px; vertical-align: middle;">email</i>
                <strong>Email-Versand:</strong> Aktiviert
              </p>
            </div>
          </v-card>

          <!-- Rechnungsadresse -->
          <v-card outlined class="pa-3 mb-3">
            <p class="mb-1 font-weight-600 text-typo">
              <i class="material-icons-round me-2" style="font-size: 18px; vertical-align: middle;">location_on</i>
              Rechnungsadresse
            </p>
            <p class="mb-0 text-sm text-typo">{{ invoiceData.firstname }} {{ invoiceData.lastname }}</p>
            <p class="mb-0 text-sm text-typo">{{ invoiceData.street }}</p>
            <p class="mb-0 text-sm text-typo">{{ invoiceData.zipcode }} {{ invoiceData.city }}</p>
            <p class="mb-0 text-sm text-typo" v-if="invoiceData.email">
              <i class="material-icons-round me-1" style="font-size: 14px; vertical-align: middle;">email</i>
              {{ invoiceData.email }}
            </p>
          </v-card>

          <!-- Bankdaten für Überweisung (nur bei Zahlungsart "Rechnung") -->
          <v-card outlined class="pa-3 mb-3" v-if="paymentMethod === 'invoice' && hasCompleteBankDetails">
            <p class="mb-1 font-weight-600 text-typo">
              <i class="material-icons-round me-2" style="font-size: 18px; vertical-align: middle;">account_balance</i>
              Bankverbindung für Überweisung (Event-Owner)
            </p>
            <v-divider class="my-2"></v-divider>
            <div class="text-sm text-typo">
              <p class="mb-1">
                <strong>Empfänger:</strong> {{ eventOwner.invoiceDetails.accountHolder }}
              </p>
              <p class="mb-1" v-if="eventOwner.invoiceDetails.bankName">
                <strong>Bank:</strong> {{ eventOwner.invoiceDetails.bankName }}
              </p>
              <p class="mb-1">
                <strong>IBAN:</strong> {{ eventOwner.invoiceDetails.iban }}
              </p>
              <p class="mb-1" v-if="eventOwner.invoiceDetails.bic">
                <strong>BIC:</strong> {{ eventOwner.invoiceDetails.bic }}
              </p>
              <p class="mb-1">
                <strong>Verwendungszweck:</strong> <span class="font-italic">Wird automatisch als RE-Nummer eingefügt</span>
              </p>
              <p class="mb-0" v-if="eventOwner.invoiceDetails.paymentTerm">
                <strong>Zahlungsziel:</strong> {{ eventOwner.invoiceDetails.paymentTerm }} Tage
              </p>
            </div>
          </v-card>

          <!-- Personalisierung Zusammenfassung -->
          <v-card outlined class="pa-3 mb-3" v-if="selectedEvent && (requiresPersonalization || usePriceLowered) && participants.length > 0">
            <p class="mb-2 font-weight-600 text-typo">
              <i class="material-icons-round me-2" style="font-size: 18px; vertical-align: middle;">badge</i>
              Teilnehmer
            </p>
            <div v-for="(participant, index) in participants" :key="index" class="mb-2">
              <v-chip 
                small 
                :color="participant.price_lowered ? 'success' : 'primary'" 
                outlined
                class="mr-2"
              >
                Ticket {{ index + 1 }}: {{ participant.name || '(leer)' }}
                <span v-if="participant.price_lowered" class="ml-1">(ermäßigt)</span>
              </v-chip>
            </div>
          </v-card>

          <!-- Warnung für inaktive Events -->
          <v-alert
            v-if="selectedEvent && (!selectedEvent.active || !selectedEvent.entries_active)"
            type="warning"
            dense
            colored-border
            border="left"
            class="mt-3 mb-0"
          >
            <small class="font-weight-600 text-typo">
              {{ !selectedEvent.active ? 'Event ist nicht öffentlich' : 'Anmeldung ist nicht öffentlich' }} - 
              Ticket wird als Reservierung erstellt
            </small>
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-4 d-flex justify-end">
          <v-btn
            text
            @click="closeConfirmDialog"
            :disabled="creating"
            class="me-2"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">close</i>
            Abbrechen
          </v-btn>
          <v-btn
            :loading="creating"
            @click="confirmAndCreateTicket"
            class="bg-gradient-primary text-white"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">check</i>
            {{ ticketQuantity > 1 ? `${ticketQuantity} Tickets erstellen` : 'Ticket erstellen' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.visible"
      :color="snackbar.color"
      :timeout="3000"
      top
      right
    >
      <strong v-if="snackbar.title">{{ snackbar.title }}</strong>
      <span v-if="snackbar.message">{{ snackbar.message }}</span>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axiosAuth from '@/shared/configs/axios-auth'
import coreData from '@/mixins/coreDataMixin'

export default {
  name: 'PosTicketCreate',
  mixins: [coreData],
  data() {
    return {
      loadingEvents: false,
      loadingEventDetails: false,
      creating: false,
      downloadingPdf: false,
      showPdfDialog: false,
      showConfirmDialog: false,
      showDefaultAddressDialog: false,
      savingDefaultAddress: false,
      showBankDetailsDialog: false,
      savingBankDetails: false,
      bankDetailsValid: false,
      bankDetailsConfirmed: false,
      formValid: false,
      selectedEventId: null,
      selectedEvent: null,
      eventOwner: null, // Store the event owner's manager data
      availableEvents: [],
      eventSearch: null,
      ticketQuantity: 1,
      paymentMethod: 'cash',
      sendEmailAfterCreation: false,
      defaultAddress: {
        firstname: '',
        lastname: '',
        street: '',
        zipcode: '',
        city: ''
      },
      bankDetails: {
        accountHolder: '',
        iban: '',
        bic: '',
        bankName: '',
        paymentTerm: 14,
        invoiceNotes: ''
      },
      paymentMethods: [
        { value: 'cash', label: 'Bar', icon: 'payments' },
        { value: 'electronic', label: 'Elektronisch vor Ort', icon: 'credit_card' },
        { value: 'invoice', label: 'Rechnung (Zahlungsziel)', icon: 'description' }
      ],
      // Rechnungsdaten (immer erforderlich)
      invoiceData: {
        firstname: '',
        lastname: '',
        street: '',
        zipcode: '',
        city: '',
        email: ''
      },
      // Personalisierung (nur wenn Event es erfordert oder Ermäßigung gewählt)
      participants: [],
      usePriceLowered: false, // Toggle für Ermäßigung (nur wenn Event keine Personalisierungs-Pflicht hat)
      useEveningPrice: false, // Toggle für Abendkassenpreis (wenn price_local verfügbar)
      // Rabatt/Aufschlag (wird auf Gesamtsumme angewendet)
      discountValue: 0,
      discountType: 'percent', // 'fixed' (EUR) oder 'percent' (%)
      discountMode: 'discount', // 'discount' (Rabatt) oder 'surcharge' (Aufschlag)
      discountReason: '',
      ticketResult: null,
      ticketResults: [],
      pdfData: null,
      paymentId: null,
      eventRules: [
        v => !!v || 'Bitte wählen Sie ein Event aus'
      ],
      quantityRules: [
        v => !!v || 'Anzahl ist erforderlich',
        v => v >= 1 || 'Mindestens 1 Ticket',
        v => v <= 50 || 'Maximal 50 Tickets auf einmal'
      ],
      emailRules: [
        v => !v || /.+@.+\..+/.test(v) || 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
      ],
      invoiceFirstnameRules: [
        v => !!v || 'Vorname ist erforderlich',
        v => (v && v.length >= 2) || 'Mindestens 2 Zeichen erforderlich'
      ],
      invoiceLastnameRules: [
        v => !!v || 'Nachname ist erforderlich',
        v => (v && v.length >= 2) || 'Mindestens 2 Zeichen erforderlich'
      ],
      nameRules: [
        v => !!v || 'Name ist erforderlich',
        v => v.split(' ').length >= 2 || 'Bitte geben Sie Vor- und Nachnamen ein'
      ],
      streetRules: [
        v => !!v || 'Straße ist erforderlich'
      ],
      zipRules: [
        v => !!v || 'PLZ ist erforderlich',
        v => (v && v.length >= 3 && v.length <= 10) || 'PLZ muss 3-10 Zeichen haben'
      ],
      cityRules: [
        v => !!v || 'Stadt ist erforderlich'
      ],
      accountHolderRules: [
        v => !!v || 'Kontoinhaber ist erforderlich',
        v => (v && v.length >= 3) || 'Mindestens 3 Zeichen erforderlich'
      ],
      ibanRules: [
        v => !!v || 'IBAN ist erforderlich',
        v => {
          if (!v) return true
          // Remove spaces and convert to uppercase
          const cleaned = v.replace(/\s/g, '').toUpperCase()
          // Basic IBAN format check: 2 letters + 2-34 digits/letters
          return /^[A-Z]{2}[0-9A-Z]{2,34}$/.test(cleaned) || 'Ungültiges IBAN-Format (z.B. DE89370400440532013000)'
        }
      ],
      bicRules: [
        v => {
          if (!v || v.trim() === '') return true // Optional field
          // BIC format: 4 letters (bank code) + 2 letters (country) + 2 alphanumeric (location) + optional 3 alphanumeric (branch)
          return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(v.toUpperCase()) || 'Ungültiges BIC-Format (z.B. COBADEFFXXX)'
        }
      ],
      paymentTermRules: [
        v => !v || (v >= 1 && v <= 90) || 'Zahlungsziel muss zwischen 1 und 90 Tagen liegen'
      ]
    }
  },
  computed: {
    ...mapGetters(['currentForManagerId', 'accessibleManagers', 'isPosUser', 'isChecker', 'userInformations', 'isAdmin', 'isRealAdmin']),

    // Preis-Berechnung: Durchschnittspreis pro Ticket (für Anzeige)
    finalPrice() {
      if (!this.selectedEvent) return 0
      return this.subtotalPrice / this.ticketQuantity
    },
    // Basis-Preis (Online oder Abendkasse)
    basePrice() {
      if (!this.selectedEvent) return 0
      return this.useEveningPrice && this.selectedEvent.price_local && this.selectedEvent.price_local > 0
        ? this.selectedEvent.price_local
        : this.selectedEvent.price
    },
    // Zwischensumme VOR Rabatt
    subtotalPrice() {
      if (!this.selectedEvent) return 0
      if (!this.requiresPersonalization || this.participants.length === 0) {
        // Ohne Personalisierung: Basis-Preis * Anzahl
        return this.basePrice * this.ticketQuantity
      }
      // Mit Personalisierung: Summe der Einzelpreise
      return this.participants.reduce((sum, p) => {
        const price = p.price_lowered && this.selectedEvent.price_lowered 
          ? this.selectedEvent.price_lowered 
          : this.basePrice
        return sum + price
      }, 0)
    },
    // Rabatt/Aufschlag in EUR
    discountAmountEuro() {
      if (!this.discountValue || this.discountValue <= 0) return 0
      let amount = 0
      if (this.discountType === 'fixed') {
        amount = this.discountValue
        // Bei Rabatt: Max = Zwischensumme
        if (this.discountMode === 'discount') {
          amount = Math.min(amount, this.subtotalPrice)
        }
      } else {
        // Prozent
        amount = (this.subtotalPrice * this.discountValue) / 100
        // Bei Rabatt: Max = 100% der Zwischensumme
        if (this.discountMode === 'discount') {
          amount = Math.min(amount, this.subtotalPrice)
        }
      }
      return amount
    },
    // Prüfen ob verschiedene Preise vorhanden sind
    hasDifferentPrices() {
      if (!this.selectedEvent) return false
      
      if (!this.requiresPersonalization || this.participants.length === 0) {
        // Ohne Personalisierung: Alle Tickets haben den gleichen Preis
        return false
      }
      
      // Prüfe ob verschiedene Preise vorhanden sind
      const prices = this.participants.map(p => {
        return p.price_lowered && this.selectedEvent.price_lowered 
          ? this.selectedEvent.price_lowered 
          : this.basePrice
      })
      
      // Prüfe ob es unterschiedliche Preise gibt
      const uniquePrices = [...new Set(prices.map(p => p.toFixed(2)))]
      return uniquePrices.length > 1
    },
    // Einzelne Ticket-Positionen für die Anzeige
    ticketPositions() {
      if (!this.selectedEvent) return []
      
      const positions = []
      
      if (!this.requiresPersonalization || this.participants.length === 0) {
        // Keine Personalisierung: Eine Position
        let description = 'normal Preis'
        if (this.useEveningPrice && this.selectedEvent.price_local && this.selectedEvent.price_local > 0) {
          description = 'Abendkassen-Preis'
        }
        positions.push({
          quantity: this.ticketQuantity,
          price: this.basePrice,
          description: description,
          isPriceLowered: false
        })
      } else {
        // Mit Personalisierung: Gruppiere nach Preis
        const priceGroups = {}
        this.participants.forEach((participant, index) => {
          const price = participant.price_lowered && this.selectedEvent.price_lowered 
            ? this.selectedEvent.price_lowered 
            : this.basePrice
          const priceKey = price.toFixed(2)
          
          if (!priceGroups[priceKey]) {
            let description = 'normal Preis'
            if (this.useEveningPrice && this.selectedEvent.price_local && this.selectedEvent.price_local > 0 && Math.abs(price - this.selectedEvent.price_local) < 0.01) {
              description = 'Abendkassen-Preis'
            }
            if (participant.price_lowered) {
              description = 'ermäßigt'
            }
            
            priceGroups[priceKey] = {
              quantity: 0,
              price: price,
              description: description,
              isPriceLowered: participant.price_lowered || false
            }
          }
          priceGroups[priceKey].quantity++
        })
        
        // Konvertiere zu Array
        Object.values(priceGroups).forEach(group => {
          positions.push(group)
        })
      }
      
      return positions
    },
    // Beschreibung für einzelne Position (wenn alle Tickets den gleichen Preis haben)
    getSinglePositionDescription() {
      if (!this.selectedEvent) return 'Ticket'
      
      if (this.useEveningPrice && this.selectedEvent.price_local && this.selectedEvent.price_local > 0) {
        return 'Abendkassen-Preis'
      }
      
      if (this.priceLoweredCount > 0 && this.priceLoweredCount === this.ticketQuantity) {
        return 'ermäßigt'
      }
      
      return 'normal Preis'
    },
    // Rabatt in Prozent
    discountPercentageValue() {
      if (!this.discountValue || this.discountValue <= 0) return 0
      if (this.discountType === 'percent') {
        return this.discountValue
      } else {
        // Fixed in Prozent umrechnen
        if (this.subtotalPrice === 0) return 0
        return (this.discountAmountEuro / this.subtotalPrice) * 100
      }
    },
    // Gesamtsumme NACH Rabatt/Aufschlag
    finalTotalPrice() {
      if (this.discountMode === 'discount') {
      return Math.max(0, this.subtotalPrice - this.discountAmountEuro)
      } else {
        // Aufschlag
        return this.subtotalPrice + this.discountAmountEuro
      }
    },
    // Legacy (entfernt)
    hasDiscount() {
      return false
    },
    discountAmount() {
      return 0
    },
    discountPercentage() {
      return 0
    },
    // Personalisierung erforderlich wenn: Event es erfordert ODER mindestens ein Ticket ermäßigt ist
    requiresPersonalization() {
      if (!this.selectedEvent) return false
      // Event erfordert Personalisierung für ALLE Tickets
      if (this.selectedEvent.personalization_required) return true
      // ODER: Ermäßigung wurde aktiviert UND mindestens ein Ticket ist ermäßigt
      // (usePriceLowered alleine reicht nicht, es muss auch tatsächlich ein Ticket ermäßigt sein)
      return this.hasAnyPriceLowered
    },
    hasAnyPriceLowered() {
      return this.participants.some(p => p.price_lowered === true)
    },
    priceLoweredCount() {
      return this.participants.filter(p => p.price_lowered === true).length
    },
    filledParticipantsCount() {
      return this.participants.filter(p => {
        const hasName = p.name && p.name.trim().length >= 3
        if (!this.selectedEvent.address_required) return hasName
        const hasAddress = p.street && p.zipcode && p.city
        return hasName && hasAddress
      }).length
    },
    // Available tickets count
    availableTicketsCount() {
      if (!this.selectedEvent || !this.selectedEvent.slots) return 0
      const entriesCount = Array.isArray(this.selectedEvent.entries) 
        ? this.selectedEvent.entries.filter(e => !e.canceled).length
        : (this.selectedEvent.entriesCount || 0)
      return Math.max(0, this.selectedEvent.slots - entriesCount)
    },
    // How many tickets MUST be personalized (either all or only discounted ones)
    requiredFilledCount() {
      if (!this.selectedEvent) return 0
      // If event requires personalization for ALL tickets
      if (this.selectedEvent.personalization_required) {
        return this.ticketQuantity
      }
      // Otherwise, only price-lowered tickets need personalization
      return this.priceLoweredCount
    },
    // Count how many REQUIRED tickets are actually filled
    actualFilledRequiredCount() {
      if (!this.selectedEvent) return 0
      
      // If event requires all tickets to be filled
      if (this.selectedEvent.personalization_required) {
        return this.filledParticipantsCount
      }
      
      // Otherwise, count only filled price-lowered tickets
      return this.participants.filter(p => {
        if (!p.price_lowered) return false // Don't count non-discounted
        const hasName = p.name && p.name.trim().length >= 3
        if (!this.selectedEvent.address_required) return hasName
        const hasAddress = p.street && p.zipcode && p.city
        return hasName && hasAddress
      }).length
    },
    // Check if all REQUIRED participants are filled
    allRequiredParticipantsFilled() {
      if (!this.requiresPersonalization) return true
      
      // If event requires personalization for ALL tickets
      if (this.selectedEvent.personalization_required) {
        return this.filledParticipantsCount >= this.ticketQuantity
      }
      
      // Otherwise: Only price-lowered tickets MUST be filled
      // Check each ticket: if it's price_lowered, it must be filled. If not, it's optional.
      const allRequiredFilled = this.participants.every(p => {
        // Non-discounted tickets are always "filled" (optional)
        if (!p.price_lowered) return true
        
        // Discounted tickets must have complete data
        const hasName = p.name && p.name.trim().length >= 3
        if (!this.selectedEvent.address_required) return hasName
        const hasAddress = p.street && p.zipcode && p.city
        return hasName && hasAddress
      })
      
      return allRequiredFilled
    },
    paymentMethodLabel() {
      const method = this.paymentMethods.find(m => m.value === this.paymentMethod)
      return method ? method.label : 'Bar'
    },
    hasDefaultAddress() {
      return this.defaultAddress.firstname && 
             this.defaultAddress.lastname && 
             this.defaultAddress.street && 
             this.defaultAddress.zipcode && 
             this.defaultAddress.city
    },
    isValidEmail() {
      if (!this.invoiceData.email) return false
      // Exclude temp.local emails (used for bulk tickets)
      if (this.invoiceData.email.includes('@temp.local')) return false
      // Validate email format
      return /.+@.+\..+/.test(this.invoiceData.email)
    },
    hasCompleteBankDetails() {
      // Use event owner's bank details instead of logged-in user's
      if (!this.eventOwner || !this.eventOwner.invoiceDetails) return false
      const inv = this.eventOwner.invoiceDetails
      // Check required fields for bank transfer: IBAN, accountHolder
      return !!(inv.iban && inv.iban.trim() && inv.accountHolder && inv.accountHolder.trim())
    },
    isEventOwner() {
      // Check if logged-in user is the actual event owner (admin: true)
      if (!this.selectedEvent || !this.userInformations) return false
      // Extract manager ID (could be populated object or just ID string)
      const managerId = typeof this.selectedEvent.manager === 'object'
        ? this.selectedEvent.manager._id
        : this.selectedEvent.manager
      return this.userInformations.admin && managerId === this.userInformations._id
    },
    validationIssues() {
      const issues = []
      
      if (!this.selectedEvent) {
        issues.push({ icon: 'event', text: 'Event auswählen', color: '#cb0c9f' })
      }
      
      if (this.selectedEvent && !this.formValid) {
        issues.push({ icon: 'error_outline', text: 'Formular unvollständig', color: '#f5365c' })
      }
      
      if (this.selectedEvent && this.requiresPersonalization && !this.allRequiredParticipantsFilled) {
        issues.push({ 
          icon: 'badge', 
          text: `Personalisierung: ${this.actualFilledRequiredCount}/${this.requiredFilledCount} ausgefüllt`, 
          color: '#fb6340' 
        })
      }
      
      if (this.paymentMethod === 'invoice' && !this.hasCompleteBankDetails) {
        issues.push({ icon: 'account_balance', text: 'Bankdaten fehlen', color: '#f5365c' })
      }
      
      return issues
    },
    canCreateTicket() {
      return this.formValid && 
             this.selectedEvent && 
             !this.creating && 
             !this.loadingEventDetails && 
             !(this.paymentMethod === 'invoice' && !this.hasCompleteBankDetails) &&
             (!this.requiresPersonalization || this.allRequiredParticipantsFilled)
    },
    discountRules() {
      return [
        (v) => {
          if (v === null || v === undefined || v === '') return true
          const numValue = parseFloat(v)
          if (isNaN(numValue)) return 'Ungültiger Wert'
          if (numValue < 0) return 'Wert darf nicht negativ sein'
          if (this.discountMode === 'discount') {
            // Rabatt: Max 100% bei Prozent, Max Zwischensumme bei Fixed
          if (this.discountType === 'percent' && numValue > 100) {
            return 'Rabatt darf nicht über 100% liegen'
          }
          if (this.discountType === 'fixed' && this.subtotalPrice > 0 && numValue > this.subtotalPrice) {
            return `Rabatt darf nicht über ${this.subtotalPrice.toFixed(2)} € liegen`
          }
          }
          // Aufschlag: Keine Obergrenze
          return true
        }
      ]
    }
  },
  methods: {
    getCurrentManagerName() {
      // For real admins, show their own company name
      if (this.isRealAdmin && this.userInformations) {
        if (this.currentForManagerId) {
          const manager = this.accessibleManagers.find(m => m.id === this.currentForManagerId)
          if (manager) return manager.name
        }
        // Fallback for real admin: show own company name
        return this.userInformations.companyName || 
               `${this.userInformations.firstname} ${this.userInformations.lastname}` || 
               this.userInformations.email || 
               'Admin'
      }
      
      // For users with roles
      const manager = this.accessibleManagers.find(m => m.id === this.currentForManagerId)
      return manager ? manager.name : 'Unbekannt'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    getAvailableTicketsStyle() {
      if (this.availableTicketsCount === 0) {
        return 'border-left: 4px solid #f5365c; background-color: #fff0f0;'
      } else if (this.availableTicketsCount <= 10) {
        return 'border-left: 4px solid #fb6340; background-color: #fff5f3;'
      } else {
        return 'border-left: 4px solid #2dce89; background-color: #e7f6f8;'
      }
    },
    getAvailableTicketsColor() {
      if (this.availableTicketsCount === 0) {
        return '#f5365c'
      } else if (this.availableTicketsCount <= 10) {
        return '#fb6340'
      } else {
        return '#2dce89'
      }
    },

    async loadEventsForDropdown() {
      console.log('🔍 [POS] loadEventsForDropdown called');

      // Real admins always have access, otherwise check for POS, Manager, or Admin role
      // Users with multiple roles (e.g. Checker + POS/Manager) can access POS
      const hasPosAccess = this.isRealAdmin || this.isPosUser || this.isManager || this.isAdmin;
      if (!this.currentForManagerId || !hasPosAccess) {
        console.log('🚫 [POS] Permission check failed - redirecting back');
        this.SnackbarShow('Danger', '', 'Sie haben keine Berechtigung, POS-Tickets zu erstellen')
        this.$router.go(-1)
        return
      }

      console.log('✅ [POS] Permission check passed - loading events');

      this.loadingEvents = true
      try {
        // Load events without year filter (POS needs future events across multiple years)
        // Using /admin/getAll for better performance (optimized query with entry counts)
        const response = await axiosAuth.get('/events/admin/getAll', {
          params: { 
            includeArchived: false
            // No year filter - POS will filter for future events in frontend
          }
        })
        
        console.log('📊 [POS] Events loaded:', response.data.length);
        
        // Nur zukünftige Events filtern (auch inaktive, damit Reservierungen möglich sind)
        this.availableEvents = response.data.filter(event => {
          // Nur zukünftige Events
          const eventDate = new Date(event.date)
          const now = new Date()
          
          // Filter für Events des aktuellen forManagers (falls mehrere Manager-Kontexte)
          // ALWAYS use currentForManagerId (even for real admins in external context)
          const targetManagerId = this.currentForManagerId || this.userInformations._id;
          const eventManagerId = event.manager?._id || event.manager;
          
          const isForCurrentManager = eventManagerId && (
            eventManagerId.toString() === targetManagerId.toString()
          );
          
          console.log(`🔍 [POS] Event "${event.title}": eventDate=${eventDate.toISOString()}, now=${now.toISOString()}, future=${eventDate > now}, isForCurrentManager=${isForCurrentManager}, eventManagerId=${eventManagerId}, targetManagerId=${targetManagerId}, currentForManagerId=${this.currentForManagerId}, userInformations._id=${this.userInformations._id}`);
          
          // WICHTIG: Auch inaktive Events sind auswählbar (für Reservierungen)
          return eventDate > now && isForCurrentManager;
        })
        
        console.log('✅ [POS] Available events after filter:', this.availableEvents.length);
      } catch (error) {
        console.error('❌ [POS] Error loading events:', error)
        this.SnackbarShow('Danger', '', 'Fehler beim Laden der Events')
      } finally {
        this.loadingEvents = false
      }
    },

    initializeParticipants() {
      // Initialisiere participants Array basierend auf ticketQuantity
      if (!this.selectedEvent) {
        this.participants = []
        return
      }

      const quantity = parseInt(this.ticketQuantity) || 1
      
      // Wenn Personalisierung erforderlich ODER Ermäßigung aktiviert ODER Ermäßigung möglich
      if (this.selectedEvent.personalization_required || this.usePriceLowered || (this.selectedEvent.price_lowered && this.selectedEvent.price_lowered > 0)) {
        // Behalte existierende Daten wenn möglich
        const existingParticipants = [...this.participants]
        this.participants = []
        
        for (let i = 0; i < quantity; i++) {
          this.participants.push(existingParticipants[i] || {
            name: '',
            street: '',
            zipcode: '',
            city: '',
            price_lowered: false
          })
        }
      } else {
        this.participants = []
      }
      
      console.log('✅ [POS] Initialized participants:', this.participants.length)
    },

    onUsePriceLoweredChange() {
      // Wenn Ermäßigung aktiviert wird → Personalisierung initialisieren
      console.log('🎫 [POS] Use price lowered toggled:', this.usePriceLowered)
      if (this.usePriceLowered) {
        this.initializeParticipants()
      } else {
        // Bei Deaktivierung: Participants leeren
        this.participants = []
      }
    },

    onPriceLoweredChange() {
      // Wenn irgendein Teilnehmer ermäßigt ist, muss Personalisierung ausgefüllt werden
      console.log('💰 [POS] Price lowered changed:', this.hasAnyPriceLowered)
    },

    async loadEventDetails(eventId) {
      if (!eventId) {
        this.selectedEvent = null
        this.participants = []
        this.usePriceLowered = false
        return
      }

      console.log('📥 [POS] Loading event details for:', eventId);
      
      this.loadingEventDetails = true
      try {
        const response = await axiosAuth.get(`/events/byIdAdmin/${eventId}`)
        this.selectedEvent = response.data
        
        // Load default address from event owner (not from logged-in manager)
        await this.loadEventOwnerDefaultAddress()
        
        // Participants Array initialisieren
        this.initializeParticipants()
        
        console.log('✅ [POS] Event details loaded:', this.selectedEvent.title);
      } catch (error) {
        console.error('❌ [POS] Error loading event details:', error)
        this.SnackbarShow('Danger', '', 'Fehler beim Laden der Event-Details')
        this.selectedEvent = null
        this.participants = []
      } finally {
        this.loadingEventDetails = false
      }
    },

    showConfirmationModal() {
      // Validate required fields
      if (!this.selectedEvent) {
        this.SnackbarShow('Warning', '', 'Bitte wähle zuerst ein Event aus.')
        return
      }

      // Check invoice data
      if (!this.invoiceData.firstname || !this.invoiceData.lastname || !this.invoiceData.street || !this.invoiceData.zipcode || !this.invoiceData.city) {
        this.SnackbarShow('Warning', '', 'Bitte fülle alle Pflichtfelder der Rechnungsadresse aus (Vorname, Nachname, Straße, PLZ, Stadt).')
        return
      }

      // Check participants if personalization required (Event-Pflicht oder Ermäßigung gewählt)
      if (this.requiresPersonalization && !this.allRequiredParticipantsFilled) {
        const message = this.selectedEvent.personalization_required
          ? `Bitte fülle alle Teilnehmerdaten vollständig aus (${this.actualFilledRequiredCount}/${this.ticketQuantity}).`
          : `Bitte fülle die Daten für alle ermäßigten Tickets vollständig aus (${this.actualFilledRequiredCount}/${this.requiredFilledCount}).`
        this.SnackbarShow('Warning', '', message)
        return
      }

      // Check bank details if payment method is invoice
      if (this.paymentMethod === 'invoice' && !this.hasCompleteBankDetails) {
        this.SnackbarShow('Warning', 'Bankdaten fehlen', 'Für Zahlungsart "Rechnung" müssen IBAN und Kontoinhaber im Profil hinterlegt werden.')
        return
      }

      // Show confirmation dialog
      this.showConfirmDialog = true
    },

    closeConfirmDialog() {
      this.showConfirmDialog = false
    },

    async confirmAndCreateTicket() {
      // Validation already done in showConfirmationModal
      await this.createTicket()
      this.closeConfirmDialog()
    },

    async createTicket() {
      // Validation already done in showConfirmationModal
      this.creating = true
      try {
        const payload = {
          eventId: this.selectedEventId,
          quantity: this.ticketQuantity,
          paymentMethod: this.paymentMethod,
          sendEmail: this.sendEmailAfterCreation,
          // Rechnungsdaten (immer erforderlich)
          invoiceData: this.invoiceData,
          // Personalisierung: Entweder voll (mit Ermäßigung) oder nur Namen (legacy)
          // Send participants if: Event requires it OR any ticket is actually price-lowered
          participants: (this.selectedEvent.personalization_required || this.hasAnyPriceLowered) ? this.participants : null,
          // Legacy fallback: not needed anymore, backend handles both cases
          participantNames: null,
          // Preis-Auswahl: Online oder Abendkasse
          useEveningPrice: this.useEveningPrice && this.selectedEvent.price_local && this.selectedEvent.price_local > 0,
          // Rabatt/Aufschlag (optional)
          discount: this.discountValue > 0 ? {
            value: this.discountValue,
            type: this.discountType, // 'fixed' oder 'percent'
            mode: this.discountMode, // 'discount' | 'surcharge'
            reason: this.discountReason || null
          } : null
        }

        const response = await axiosAuth.post('/events/pos/create-ticket', payload)

        // Bulk oder Single Response
        if (response.data.tickets) {
          this.ticketResults = response.data.tickets
          this.ticketResult = response.data.tickets[0] // Für Dialog-Kompatibilität
        } else {
          this.ticketResult = response.data.ticket
          this.ticketResults = [response.data.ticket]
        }

        this.pdfData = response.data.pdf
        this.paymentId = response.data.payment_id
        this.showPdfDialog = true

        const message = this.ticketQuantity > 1 
          ? `${this.ticketQuantity} POS-Tickets erfolgreich erstellt!`
          : 'POS-Ticket erfolgreich erstellt!'
        this.SnackbarShow('Success', '', message)

        // Formular leeren, aber Event-Auswahl beibehalten
        this.resetFormFields()

      } catch (error) {
        console.error('Error creating POS ticket:', error)
        const message = (error.response && error.response.data && error.response.data.message) || 'Fehler beim Erstellen des Tickets'
        this.SnackbarShow('Danger', '', message)
      } finally {
        this.creating = false
      }
    },

    async downloadInvoice() {
      if (!this.paymentId) {
        this.SnackbarShow('Danger', '', 'Keine Zahlungs-ID vorhanden')
        return
      }

      this.downloadingPdf = true
      try {
        console.log('📥 Downloading invoice for payment_id:', this.paymentId)
        
        const response = await axiosAuth.get(`/invoices/download/${this.paymentId}`, { 
          responseType: 'blob' 
        })

        const pdfBlob = new Blob([response.data], { type: 'application/pdf' })
        const url = URL.createObjectURL(pdfBlob)
        const link = document.createElement('a')
        link.href = url
        
        const invoiceNr = this.ticketResults[0] ? this.ticketResults[0].invoiceNr : 'POS_Ticket'
        link.download = `${invoiceNr}_Rechnung_Tickets.pdf`
        
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)

        this.SnackbarShow('Success', '', 'Rechnung erfolgreich heruntergeladen')
        this.closeDialog()

      } catch (error) {
        console.error('❌ Error downloading invoice:', error)
        this.SnackbarShow('Danger', '', 'Fehler beim Herunterladen der Rechnung')
      } finally {
        this.downloadingPdf = false
      }
    },

    closeDialog() {
      this.showPdfDialog = false
      this.ticketResult = null
      this.ticketResults = []
      this.pdfData = null
      this.paymentId = null
    },
    goToTickets() {
      this.closeDialog()
      if (this.selectedEvent && this.selectedEvent._id) {
        this.$router.push(`/admin/events/details/${this.selectedEvent._id}`)
      } else {
        this.$router.push('/admin/events')
      }
    },

    async loadDefaultAddress() {
      // This is still used for the modal to manage the logged-in user's own default address
      try {
        const response = await axiosAuth.get('/authnews/me')
        if (response.data && response.data.posDefaultAddress) {
          this.defaultAddress = {
            firstname: response.data.posDefaultAddress.firstname || '',
            lastname: response.data.posDefaultAddress.lastname || '',
            street: response.data.posDefaultAddress.street || '',
            zipcode: response.data.posDefaultAddress.zipcode || '',
            city: response.data.posDefaultAddress.city || ''
          }
        }
      } catch (error) {
        console.error('Error loading default address:', error)
      }
    },

    async loadEventOwnerDefaultAddress() {
      // Load default address AND bank details from the event owner
      if (!this.selectedEvent || !this.selectedEvent.manager) {
        return
      }

      try {
        // Extract ID from manager (could be populated object or just ID string)
        const ownerId = typeof this.selectedEvent.manager === 'object' 
          ? this.selectedEvent.manager._id 
          : this.selectedEvent.manager
        console.log('📥 [POS] Loading default address & bank details for event owner:', ownerId)
        
        // Load the event owner's manager details
        const response = await axiosAuth.get(`/authnews/manager/${ownerId}`)
        
        // Store the event owner data
        this.eventOwner = response.data
        
        // Load default address
        if (response.data && response.data.posDefaultAddress) {
          this.defaultAddress = {
            firstname: response.data.posDefaultAddress.firstname || '',
            lastname: response.data.posDefaultAddress.lastname || '',
            street: response.data.posDefaultAddress.street || '',
            zipcode: response.data.posDefaultAddress.zipcode || '',
            city: response.data.posDefaultAddress.city || ''
          }
          console.log('✅ [POS] Event owner default address loaded')
        } else {
          console.log('ℹ️ [POS] No default address found for event owner')
          // Reset to empty if owner has no default address
          this.defaultAddress = {
            firstname: '',
            lastname: '',
            street: '',
            zipcode: '',
            city: ''
          }
        }
        
        // Load bank details
        if (response.data && response.data.invoiceDetails) {
          console.log('✅ [POS] Event owner bank details loaded')
        } else {
          console.log('ℹ️ [POS] No bank details found for event owner')
        }
      } catch (error) {
        console.error('❌ [POS] Error loading event owner data:', error)
        // Reset to empty on error
        this.eventOwner = null
        this.defaultAddress = {
          firstname: '',
          lastname: '',
          street: '',
          zipcode: '',
          city: ''
        }
      }
    },

    fillWithDefaultAddress() {
      if (this.hasDefaultAddress) {
        this.invoiceData.firstname = this.defaultAddress.firstname
        this.invoiceData.lastname = this.defaultAddress.lastname
        this.invoiceData.street = this.defaultAddress.street
        this.invoiceData.zipcode = this.defaultAddress.zipcode
        this.invoiceData.city = this.defaultAddress.city
        this.SnackbarShow('Success', '', 'Default-Adresse wurde übernommen')
      }
    },

    openDefaultAddressDialog() {
      // Load current default address
      this.loadDefaultAddress()
      this.showDefaultAddressDialog = true
    },

    closeDefaultAddressDialog() {
      this.showDefaultAddressDialog = false
    },

    async saveDefaultAddress() {
      if (!this.$refs.defaultAddressForm.validate()) {
        return
      }

      this.savingDefaultAddress = true
      try {
        await axiosAuth.patch('/authnews/me', {
          posDefaultAddress: this.defaultAddress
        })

        this.SnackbarShow('Success', 'Gespeichert!', 'Default-Adresse wurde erfolgreich gespeichert.')
        this.showDefaultAddressDialog = false
      } catch (error) {
        console.error('Error saving default address:', error)
        this.SnackbarShow('Danger', 'Fehler!', 'Default-Adresse konnte nicht gespeichert werden.')
      } finally {
        this.savingDefaultAddress = false
      }
    },

    // Bankdaten Methods
    loadBankDetails() {
      // Load bank details from the event owner
      // Permission is checked in openBankDetailsDialog() before this is called
      if (this.eventOwner && this.eventOwner.invoiceDetails) {
        const inv = this.eventOwner.invoiceDetails
        this.bankDetails = {
          accountHolder: inv.accountHolder || '',
          iban: inv.iban || '',
          bic: inv.bic || '',
          bankName: inv.bankName || '',
          paymentTerm: inv.paymentTerm || 14,
          invoiceNotes: inv.invoiceNotes || ''
        }
      }
    },

    openBankDetailsDialog() {
      if (!this.isEventOwner) {
        this.SnackbarShow('Warning', 'Keine Berechtigung', 'Nur der Event-Owner kann Bankdaten verwalten.')
        return
      }
      this.loadBankDetails()
      this.bankDetailsConfirmed = false
      this.showBankDetailsDialog = true
    },

    closeBankDetailsDialog() {
      this.showBankDetailsDialog = false
      this.bankDetailsConfirmed = false
    },

    formatIBAN() {
      // Format IBAN with spaces every 4 characters for better readability
      if (this.bankDetails.iban) {
        const cleaned = this.bankDetails.iban.replace(/\s/g, '').toUpperCase()
        const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned
        this.bankDetails.iban = formatted
      }
    },

    async saveBankDetails() {
      if (!this.$refs.bankDetailsForm.validate()) {
        return
      }

      if (!this.bankDetailsConfirmed) {
        this.SnackbarShow('Warning', 'Bestätigung erforderlich', 'Bitte bestätige die Richtigkeit der Bankdaten.')
        return
      }

      this.savingBankDetails = true
      try {
        // Clean IBAN (remove spaces) before saving
        const cleanedIban = this.bankDetails.iban.replace(/\s/g, '').toUpperCase()
        
        await axiosAuth.patch('/authnews/me', {
          invoiceDetails: {
            ...this.bankDetails,
            iban: cleanedIban,
            bic: this.bankDetails.bic ? this.bankDetails.bic.toUpperCase() : ''
          }
        })

        // Update local user informations
        await this.$store.dispatch('userInformations')

        this.SnackbarShow('Success', 'Gespeichert!', 'Bankdaten wurden erfolgreich gespeichert und können nun für Rechnungen verwendet werden.')
        this.showBankDetailsDialog = false
        this.bankDetailsConfirmed = false
      } catch (error) {
        console.error('Error saving bank details:', error)
        this.SnackbarShow('Danger', 'Fehler!', 'Bankdaten konnten nicht gespeichert werden. Bitte prüfe deine Eingaben.')
      } finally {
        this.savingBankDetails = false
      }
    },

    resetForm() {
      // Complete form reset (used when context changes)
      console.log('🔄 [POS] Resetting form completely...');
      this.selectedEventId = null;
      this.selectedEvent = null;
      this.eventOwner = null;
      this.ticketQuantity = 1;
      this.paymentMethod = 'cash';
      this.sendEmailAfterCreation = false;
      this.usePriceLowered = false;
      this.useEveningPrice = false;
      this.discountValue = 0;
      this.discountType = 'percent';
      this.discountMode = 'discount';
      this.discountReason = '';
      this.invoiceData = {
        firstname: '',
        lastname: '',
        street: '',
        zipcode: '',
        city: '',
        email: ''
      };
      this.participants = [];
      // Reset validation state of the form
      if (this.$refs.ticketForm) {
        this.$refs.ticketForm.resetValidation();
      }
    },

    resetFormFields() {
      // Reset only form fields, but keep event selection
      console.log('🔄 [POS] Resetting form fields (keeping event selection)...');
      this.ticketQuantity = 1;
      this.paymentMethod = 'cash';
      this.sendEmailAfterCreation = false;
      this.usePriceLowered = false;
      this.useEveningPrice = false;
      this.discountValue = 0;
      this.discountType = 'percent';
      this.discountMode = 'discount';
      this.discountReason = '';
      this.invoiceData = {
        firstname: '',
        lastname: '',
        street: '',
        zipcode: '',
        city: '',
        email: ''
      };
      this.participants = [];
      
      // Re-initialize participants if event requires personalization
      if (this.selectedEvent) {
        this.initializeParticipants();
      }
      
      // Reset validation state of the form
      if (this.$refs.ticketForm) {
        this.$refs.ticketForm.resetValidation();
      }
    }
  },

  watch: {
    currentForManagerId(newVal, oldVal) {
      // Reset form when context (manager) changes
      if (newVal !== oldVal && oldVal !== undefined) {
        console.log('🔄 [POS] Context changed, resetting form:', { oldVal, newVal })
        this.resetForm()
        // Reload events for new context
        this.loadEventsForDropdown()
      }
    },
    selectedEventId(newVal, oldVal) {
      if (newVal !== oldVal) {
        // Event-Details laden wenn ID sich ändert
        this.loadEventDetails(newVal)
      }
    },
    ticketQuantity(newVal) {
      // Passe participants Array Länge an
      if (this.selectedEvent && (this.requiresPersonalization || this.usePriceLowered)) {
        this.initializeParticipants()
      }
    },
    isValidEmail(newVal) {
      // Activate email sending checkbox if email becomes valid
      if (newVal) {
        this.sendEmailAfterCreation = true
      } else if (this.sendEmailAfterCreation) {
        // Deactivate email sending checkbox if email becomes invalid
        this.sendEmailAfterCreation = false
      }
    },
    discountValue(newVal) {
      if (newVal === null || newVal === undefined) return
      const numValue = parseFloat(newVal)
      if (isNaN(numValue)) return
      
      // Limit discount to 100% when type is percent
      if (this.discountType === 'percent' && numValue > 100) {
        this.$nextTick(() => {
          this.discountValue = 100
        })
      }
      
      // Limit discount to subtotal when type is fixed (Euro)
      if (this.discountType === 'fixed' && this.subtotalPrice > 0 && numValue > this.subtotalPrice) {
        this.$nextTick(() => {
          this.discountValue = this.subtotalPrice
        })
      }
    },
    subtotalPrice(newVal) {
      // Adjust discount if subtotal changes and discount is higher than new subtotal
      if (this.discountType === 'fixed' && this.discountValue > 0 && newVal > 0 && this.discountValue > newVal) {
        this.$nextTick(() => {
          this.discountValue = newVal
        })
      }
    }
  },
  mounted() {
    this.loadEventsForDropdown()
    this.loadDefaultAddress()
    // Bank details are only loaded when needed (when opening the dialog)
  }
}
</script>

<style scoped>
.card-shadow {
  box-shadow: 0 20px 27px 0 rgba(0, 0, 0, 0.05) !important;
}

.border-radius-xl {
  border-radius: 0.75rem !important;
}

/* Fix search icon position in autocomplete */
.pos-event-autocomplete >>> .v-input__prepend-inner {
  margin-top: 6px !important;
  padding-top: 0 !important;
  align-self: center !important;
}

/* Globale einheitliche Höhe für ALLE Input-Felder auf dieser Seite */
.v-text-field--outlined >>> .v-input__control,
.v-select--outlined >>> .v-input__control,
.v-autocomplete--outlined >>> .v-input__control {
  min-height: 40px !important;
}

.v-text-field--outlined >>> .v-input__slot,
.v-select--outlined >>> .v-input__slot,
.v-autocomplete--outlined >>> .v-input__slot {
  min-height: 40px !important;
  padding: 0 12px !important;
}

/* ALLE Icons vertikal zentrieren */
.v-text-field >>> .v-input__append-inner,
.v-text-field >>> .v-input__prepend-inner,
.v-select >>> .v-input__append-inner,
.v-select >>> .v-input__prepend-inner,
.v-autocomplete >>> .v-input__append-inner,
.v-autocomplete >>> .v-input__prepend-inner {
  margin-top: 0 !important;
  padding-top: 0 !important;
  align-self: center !important;
  display: flex !important;
  align-items: center !important;
}

/* Icon-Button im Append */
.v-text-field >>> .v-input__append-inner .v-btn {
  margin: 0 !important;
}

/* Selection Content zentrieren */
.v-select >>> .v-select__selection,
.v-autocomplete >>> .v-select__selection {
  margin-top: 0 !important;
  display: flex !important;
  align-items: center !important;
}
</style>